import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { PageLayout } from '@/components/layout/PageLayout';
import { LoadingPage, AccessDeniedPage } from '@/components/layout/StatusPages';
import { SessionPageHeader } from '@/components/sessions/SessionPageHeader';
import { TemplateSelector } from '@/components/sessions/TemplateSelector';
import { PositionSelector } from '@/components/sessions/PositionSelector';
import { CandidateFields } from '@/components/sessions/CandidateFields';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { DateTimePicker, toIsoStringOrNull } from '@/components/sessions/DateTimePicker';
import { sessionService } from '@/services/sessionService';
import type { PageProps } from '@/types/page';
import type { Session, SessionTemplate, Position } from '@arrowise/shared';
import { useAuth } from '@/providers/AuthProvider';

export default function ManageSessionPage({ nav = true, footer = true }: PageProps) {
    const { id } = useParams<{ id: string }>();
    const { teams, user } = useAuth();
    const [_session, setSession] = useState<Session | null>(null);
    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [accessDenied, setAccessDenied] = useState(false);
    const [dueAt, setDueAt] = useState<Date | null>(null);
    const [candidate, setCandidate] = useState({ name: '', email: '' });
    const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState<SessionTemplate | null>(null);
    const [selectedPosition, setSelectedPosition] = useState<Position | null>(null);

    // Helper function to check if user can manage this session
    const canManageSession = (sessionData: Session): boolean => {
        if (!user) return false;

        // Individual session: only creator can manage
        if (!sessionData.teamId) {
            return sessionData.createdBy === user.id;
        }

        // Team session: only owner/admin can manage
        const userTeam = teams?.find(t => t.team.id === sessionData.teamId);
        return userTeam?.role === 'owner' || userTeam?.role === 'admin';
    };

    useEffect(() => {
        if (!id) return;
        (async () => {
            try {
                const s = await sessionService.getSessionById(id);

                // Check if user has permission to manage this session
                if (!canManageSession(s)) {
                    setAccessDenied(true);
                    setInitialLoading(false);
                    return;
                }

                setSession(s);
                setDueAt(s.dueAt ? new Date(s.dueAt) : null);
                setCandidate({ name: s.candidateName || '', email: s.candidateEmail || '' });

                // Set selected template from session
                if (s.templateId) {
                    setSelectedTemplate({ id: s.templateId, name: `Template: ${s.templateId}` } as SessionTemplate);
                }

                // Set selected position from session
                if (s.positionId && s.position) {
                    setSelectedPosition(s.position);
                } else if (s.positionId) {
                    // If positionId exists but no position object, create a minimal one
                    setSelectedPosition({ id: s.positionId, name: `Position: ${s.positionId}` } as Position);
                }

                setAccessDenied(false);
            } catch (err: any) {
                toast.error(err?.message || 'Failed to load session');
            } finally {
                setInitialLoading(false);
            }
        })();
    }, [id, teams, user]);

    async function handleSave(e: React.FormEvent) {
        e.preventDefault();
        if (!id) return;
        setLoading(true);
        try {
            const payload = {
                dueAt: toIsoStringOrNull(dueAt) || undefined,
                templateId: selectedTemplate?.id,
                positionId: selectedPosition?.id
            } as any;
            const updated = await sessionService.updateSession(id, payload);
            setSession(updated);
            toast.success('Session updated');
        } catch (err: any) {
            toast.error(err?.message || 'Failed to update session');
        } finally {
            setLoading(false);
        }
    }

    async function handleSaveParticipants() {
        if (!id) return;
        if (!_session?.teamId) return; // only for team sessions
        setLoading(true);
        try {
            const updatedSession = await sessionService.updateParticipants(id, {
                candidate: { name: candidate.name, email: candidate.email }
            });
            setSession(updatedSession);
            toast.success('Participants updated');
        } catch (err: any) {
            toast.error(err?.message || 'Failed to update participants');
        } finally {
            setLoading(false);
        }
    }

    // Show loading state while checking permissions
    if (initialLoading) {
        return <LoadingPage nav={nav} footer={footer} message="Loading session..." />;
    }

    // Show access denied if user doesn't have permission
    if (accessDenied) {
        return (
            <AccessDeniedPage
                nav={nav}
                footer={footer}
                message="You don't have permission to manage this session. Only session creators (for individual sessions) or team owners/admins (for team sessions) can manage sessions."
            />
        );
    }

    return (
        <PageLayout nav={nav} footer={footer}>
            <div className="px-4 py-10 mx-auto max-w-4xl w-full space-y-8">
                <SessionPageHeader
                    title="Manage Session"
                    description="Update session details and schedule."
                />

                <Card>
                    <CardContent>
                        <PositionSelector
                            selectedPosition={selectedPosition}
                            onPositionSelect={setSelectedPosition}
                            teamId={_session?.teamId ?? null}
                            required={true}
                        />
                        <div className='mb-6' />

                        <TemplateSelector
                            selectedTemplate={selectedTemplate}
                            onTemplateSelect={setSelectedTemplate}
                            teamId={_session?.teamId ?? null}
                            dialogOpen={templateDialogOpen}
                            onDialogOpenChange={setTemplateDialogOpen}
                            isManage={true}
                        />
                        <div className="space-y-4">
                            <CandidateFields
                                candidate={candidate}
                                onCandidateChange={setCandidate}
                                editable={!!_session?.teamId} // Only editable for team sessions
                                useParticipantsEditor={!!_session?.teamId}
                            />

                            {_session?.teamId && (
                                <div className="flex items-center gap-2">
                                    <Button onClick={handleSaveParticipants} hidden={true} disabled={loading}>
                                        Save Participants
                                    </Button>
                                </div>
                            )}
                        </div>

                        <form className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6" onSubmit={handleSave}>
                            <div className="md:col-span-2 flex flex-col gap-2">
                                <Label>Due date</Label>
                                <DateTimePicker value={dueAt} onChange={setDueAt} />
                            </div>
                            <div className="md:col-span-2 flex items-center gap-2">
                                <Button type="submit" disabled={loading || !selectedTemplate?.id || !selectedPosition?.id}>Save</Button>
                            </div>
                        </form>
                    </CardContent>
                </Card>
            </div>
        </PageLayout>
    );
}
